import { GameState } from "@lib/titan2-slingo/dist/api/model/enum/GameState";
import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { ControlPanel } from "@lib/titan2-slingo/dist/game/view/abstract/display/ControlPanel";
import { TitanButton } from "@lib/titan2/dist/game/control/TitanButton";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { SoundEvent } from "../../../controller/event/GameEvent";
import { FeatureType } from "../../../model/GameModel";
import { GameLang } from "../lang/GameLang";
/**
 * GameControlPanel.ts
 */
export class GameControlPanel extends ControlPanel {
    create() {
        super.create();
        this.collectIcons = {
            5: TitanSprite.createSprite(this, "5_slingos_icon", this.landscapeProperties.collectIcons[5], this.portraitProperties?.collectIcons[5]),
            6: TitanSprite.createSprite(this, "6_slingos_icon", this.landscapeProperties.collectIcons[6], this.portraitProperties?.collectIcons[6]),
            7: TitanSprite.createSprite(this, "7_slingos_icon", this.landscapeProperties.collectIcons[7], this.portraitProperties?.collectIcons[7]),
            8: TitanSprite.createSprite(this, "8_slingos_icon", this.landscapeProperties.collectIcons[8], this.portraitProperties?.collectIcons[8]),
            9: TitanSprite.createSprite(this, "9_slingos_icon", this.landscapeProperties.collectIcons[9], this.portraitProperties?.collectIcons[9]),
            10: TitanSprite.createSprite(this, "10_slingos_icon", this.landscapeProperties.collectIcons[10], this.portraitProperties?.collectIcons[10]),
        };
        this.hideAllCollectIcons();
        this.payTable.setPopup(true);
    }
    handleMouseBlockerSelected() {
        this.closeAllWithEvent();
    }
    closeAllWithEvent() {
        this.hideMouseBlocker();
        if (this.payTable) {
            this.payTable.hide();
            this.dispatchGame(SlingoEvent.PAY_TABLE_CLOSE);
        }
    }
    closeAll() {
        this.hideMouseBlocker();
        if (this.payTable) {
            this.payTable.hide();
        }
    }
    postCreate() {
        this.hideMouseBlocker();
        Logger.addObjectRef(this, "ControlPanel");
        this.hideCollect();
        this.featureIntroHideableItems = [
            this.spinButtonContainer,
            this.optionsMenuButton,
            this.payTableButton,
            this.stakeSelector,
            this.endGameButton,
            this.collectButton.getMovieClip(),
            this.collectButton.getTextField(),
            ...Object.values(this.collectIcons),
            this.collectButtonValueText,
        ];
    }
    hideCollect() {
        super.hideCollect();
        this.hideAllCollectIcons();
    }
    createLogo() {
        this.logo = TitanSprite.createSprite(this, "logo", this.landscapeProperties.logoProperties, this.portraitProperties?.logoProperties);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(SlingoEvent.GAME_SUMMARY, this.handleGameSummary);
    }
    handlePayTableOpen() {
        super.handlePayTableOpen();
        this.payTable.updateTransform();
    }
    handleSendCollectRequest() {
        this.dispatchGame(CommonGameEvent.COLLECT_REQUEST);
        this.dispatchGame(SoundEvent.CLICK);
        this.dispatchGame(SoundEvent.COLLECT_BONUS);
        //Stop button selected state from changing
        this.disableButton(this.collectButton);
        this.disableButton(this.purchaseSpinButton);
    }
    handleRotate(event) {
        super.handleRotate(event);
    }
    hideAllCollectIcons() {
        Object.values(this.collectIcons).forEach(icon => icon.visible = false);
    }
    showCollect() {
        if (this.model.isSpin()) {
            this.hideAllCollectIcons();
            const currentMatchedPatterns = this.model.getCurrentMatchedPatterns();
            const featureType = this.model.featureTypeFromMatches(currentMatchedPatterns);
            if (featureType === FeatureType.NONE) {
                TitanButton.showButton(this.endGameButton);
            }
            else {
                this.collectIcons[currentMatchedPatterns].visible = true;
                const collectFeatureNameMapping = {
                    5: this.langManager.getLang(GameLang.BONUS_GREEN_TITLE),
                    6: this.langManager.getLang(GameLang.BONUS_FOOD_TITLE),
                    7: this.langManager.getLang(GameLang.BONUS_RED_TITLE),
                    8: this.langManager.getLang(GameLang.BONUS_FOOD_TITLE),
                    9: this.langManager.getLang(GameLang.BONUS_PURPLE_TITLE),
                    10: this.langManager.getLang(GameLang.BONUS_GOLD_TITLE),
                };
                const bonusName = collectFeatureNameMapping[currentMatchedPatterns];
                this.collectButtonValueText.setText(bonusName, true);
                TitanButton.showButton(this.collectButton);
            }
        }
    }
    handleFreeSpin() {
        this.resetButtons();
        this.freeSpinButton.getTextField().hide();
        TitanButton.showButton(this.freeSpinButton);
        if (this.model.isAutoSpin()) {
            this.disableButton(this.freeSpinButton);
        }
        else {
            this.showStartGameAnim();
        }
    }
    handleGameSummary() {
        TitanButton.hideButton(this.purchaseSpinButton);
        this.spinButton.show();
    }
    handleStartGameButtonSelected() {
        this.dispatchGame(CommonGameEvent.START_GAME_REQUEST);
        this.stakeSelector.disable();
        this.model.setAutoSpin(true);
    }
    handleCollectRequest() {
        this.disableButton(this.collectButton);
        this.disableButton(this.endGameButton);
        this.disablePurchaseSpinButton();
    }
    resetButtons() {
        if (this.model.getState() === GameState.CHOOSE_CELL) {
            this.purchaseSpinButton.getTextField().hide();
        }
        else {
            this.purchaseSpinButton.getTextField().show();
        }
        for (let button of this.buttons) {
            TitanButton.hideButton(button);
        }
        this.spinPrice.setText(this.spinPrice.getText(), true);
        this.hideAllCollectIcons();
    }
    updateSpinPrice(amount) {
        if (amount > 0 && !this.model.isStakeToSpin()) {
            const formattedAmount = this.formatCurrency(amount);
            this.spinPrice.setText(formattedAmount, true);
            this.spinPrice.alpha = 1;
            this.replaySpinPrice.setText(formattedAmount, true);
            this.replaySpinPrice.alpha = 1;
        }
        else {
            this.spinPrice.setText("");
            this.replaySpinPrice.setText("");
            this.purchaseSpinButton.getTextField().hide();
        }
    }
}
export class GameControlPanelEvents {
}
GameControlPanelEvents.TRANSITION_TO_FEATURE_ICON_MOVE = new EventObject("GameControlPanelEvents.TRANSITION_TO_FEATURE_ICON_MOVE");
GameControlPanelEvents.TRANSITION_TO_FEATURE_FADE_OUT_FOR_FEATURE_INTRO = new EventObject("GameControlPanelEvents.TRANSITION_TO_FEATURE_FADE_OUT_FOR_INTRO");
GameControlPanelEvents.TRANSITION_TO_FEATURE_FADE_OUT_FOR_FEATURE_START = new EventObject("GameControlPanelEvents.TRANSITION_TO_FEATURE_FADE_OUT_FOR_FEATURE_START");
GameControlPanelEvents.TRANSITION_FROM_FEATURE_FADE_IN = new EventObject("GameControlPanelEvents.TRANSITION_FROM_FEATURE_FADE_IN");
//# sourceMappingURL=GameControlPanel.js.map